import { onSnapshot, doc, getDoc, setDoc, Timestamp, collection, deleteDoc } from 'firebase/firestore';
import { useState, useEffect } from 'react';
import { db, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { FetchState, Plan, User, UserSubscription } from '../global/types';
import { PLAN_AMOUNTS } from '../global/constants';
import { convertAmount } from '../utils/currencyUtils';
import dayjs from 'dayjs';
import { v4 } from 'uuid';
import { getClientInfo } from '../utils/systemUtils';
import { useDispatch, useSelector } from 'react-redux';
import { selectSubscription, setSubscription } from '../store/userDataSlice';
import { SERVICE_URL } from '../global/constants';

export function useLiveUserPlan(userId: string | null) {
    const savedSub = useSelector(selectSubscription);
    const dispatch = useDispatch();
    const [state, setState] = useState<FetchState<UserSubscription>>({
        data: savedSub,
        loading: !!userId,
        error: null,
    });

    useEffect(() => {
        if (!userId) {
            setState({ data: null, loading: false, error: null });
            return;
        }

        const ref = doc(db, 'subscriptions', userId);
        let unsubscribe: (() => void) | undefined;
        let cancelled = false;

        // Ensure the doc exists (getDoc, no listener attached yet) before subscribing (firebase-js-sdk#9267)
        (async () => {
            try {
                const existing = await getDoc(ref);
                if (!existing.exists()) {
                    const now = Timestamp.now();
                    const freePlan: Partial<UserSubscription> = {
                        id: userId,
                        plan: Plan.FREE,
                        isTrial: false,
                        trialEndDate: null,
                        startDate: now,
                        endDate: null,
                        userId,
                        createdAt: now,
                        updatedAt: now,
                    };
                    await setDoc(ref, freePlan, { merge: true });
                }
            } catch (error) {
                console.error('Error ensuring default subscription:', error);
            }

            if (cancelled) return;
            unsubscribe = onSnapshot(
                ref,
                (docSnap) => {
                    if (!docSnap.exists()) {
                        setState({ data: null, loading: false, error: null });
                        return;
                    }
                    const data = docSnap.data() as UserSubscription;
                    const serializableData = {
                        ...data,
                        startDate: data.startDate instanceof Timestamp ? data.startDate.toMillis() : data.startDate,
                        endDate: data.endDate instanceof Timestamp ? data.endDate.toMillis() : data.endDate,
                        trialEndDate: data.trialEndDate instanceof Timestamp ? data.trialEndDate.toMillis() : data.trialEndDate,
                        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toMillis() : data.createdAt,
                        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toMillis() : data.updatedAt,
                    };
                    dispatch(setSubscription(serializableData));
                    setState({ data, loading: false, error: null });
                },
                (error) => {
                    console.error('Error fetching user plan:', error);
                    setState({ data: null, loading: false, error: 'Failed to fetch user plan' });
                }
            );
        })();

        return () => {
            cancelled = true;
            unsubscribe?.();
        };
    }, [userId]);

    return state;
}

export async function startTrial(userId: string) {
    const now = Timestamp.now();
    const trialEndDate = Timestamp.fromDate(dayjs().add(14, 'day').toDate());

    const subscriptionData: Partial<UserSubscription> = {
        id: userId,
        plan: Plan.PREMIUM,
        isTrial: true,
        trialEndDate,
        startDate: now,
        endDate: null,
        userId,
        createdAt: now,
        updatedAt: now,
    };

    await setDoc(doc(db, 'subscriptions', userId), subscriptionData, { merge: true });
}

export async function initTinkoffOrder(user: User, plan: Plan, price?: number, billingPeriod: 'month' | 'year' = 'month') {
    try {
        const { id: userId } = user;

        const orderId = v4();

        // Calculate end date based on billing period
        // Yearly = 365 days, Monthly = 30 days
        let endDate = null;
        if (plan === Plan.PREMIUM) {
            const daysToAdd = billingPeriod === 'year' ? 365 : 30;
            endDate = Timestamp.fromDate(dayjs().add(daysToAdd, 'day').toDate());
        }

        const order = {
            userId,
            plan,
            isTrial: false,
            startDate: Timestamp.now(),
            endDate: endDate,
            orderDate: Timestamp.now(),
            id: orderId,
            status: 'PENDING',
            amount: price ? Number(price * 100) : undefined // Store amount in order if needed
        };
        await setDoc(doc(db, 'orders', orderId), order, { merge: true });

        const { os, browser, device } = getClientInfo();

        const usdAmount = price !== undefined ? price : PLAN_AMOUNTS[plan];

        let amount = plan === Plan.FREE ? 0 : Math.round(await convertAmount(usdAmount, 'USD', 'RUB'));

        // Fixed price for Premium plan in RUB
        if (plan === Plan.PREMIUM) {
            amount = (billingPeriod === 'year') ? 7990 : 799;
        }

        const kopecksAmount = Number(amount * 100);

        const payload = {
            amount: kopecksAmount,
            order_id: orderId,
            description: `Subscription for user ${user.fullName || user.email} (${billingPeriod})`,
            name: user.fullName,
            email: user.email,
            success_url: `${window.location.origin}/payment-status?status=success&plan=${plan}`,
            fail_url: `${window.location.origin}/payment-status?status=fail&plan=${plan}`,
            os,
            browser,
            device,
            items: [
                {
                    name: `Subscription ${plan} (${billingPeriod})`,
                    quantity: 1,
                    price: kopecksAmount,
                },
            ],
            customerKey: userId
        };

        const initPayment = httpsCallable(functions, 'initTinkoffPayment');
        const response = await initPayment(payload);
        const result = response.data as any;

        if (!result.Success) {
            throw new Error(`Failed to initialize Tinkoff order: ${result.Message || result.Details}`);
        }

        return {
            orderId,
            paymentUrl: result.PaymentURL,
        };
    } catch (error) {
        console.error('Error initializing Tinkoff order:', error);
        throw new Error('Failed to initialize Tinkoff order');
    }
}

export async function changeSubscriptionPlan(subscription: UserSubscription | null, newPlan: Plan) {
    if (!subscription) return;

    const now = Timestamp.now();
    const { userId } = subscription;

    const endDate = newPlan === Plan.PREMIUM ? Timestamp.fromDate(dayjs().add(30, 'day').toDate()) : null;

    const subscriptionData: Partial<UserSubscription> = {
        id: userId,
        plan: newPlan,
        isTrial: false,
        startDate: now,
        endDate,
        userId,
        updatedAt: now,
    };

    await setDoc(doc(db, 'subscriptions', userId), subscriptionData, { merge: true });
}

export async function cancelSubscription(subscription: UserSubscription | null) {
    if (!subscription) return;

    const now = Timestamp.now();
    const { userId } = subscription;

    const subscriptionData: Partial<UserSubscription> = {
        id: userId,
        plan: Plan.FREE,
        isTrial: false,
        startDate: now,
        endDate: null,
        userId,
        updatedAt: now,
    };

    await setDoc(doc(db, 'subscriptions', userId), subscriptionData, { merge: true });
}

export function hasTrialEnded(subscription: UserSubscription | null) {
    return subscription?.trialEndDate && dayjs(subscription.trialEndDate.toDate()).isAfter(dayjs());
}

export function useFetchPaymentMethods(userId: string | null) {
    const [state, setState] = useState<FetchState<any[]>>({
        data: [],
        loading: !!userId,
        error: null,
    });

    useEffect(() => {
        if (!userId) {
            setState({ data: [], loading: false, error: null });
            return;
        }

        const ref = collection(db, 'users', userId, 'payment_methods');
        const unsubscribe = onSnapshot(ref, (snapshot) => {
            const methods = snapshot.docs.map(doc => doc.data());
            setState({ data: methods, loading: false, error: null });
        }, (error) => {
            console.error(error);
            setState({ data: [], loading: false, error: 'Failed to fetch payment methods' });
        });

        return () => unsubscribe();
    }, [userId]);

    return state;
}

export async function deletePaymentMethod(userId: string, methodId: string) {
    await deleteDoc(doc(db, 'users', userId, 'payment_methods', methodId));
}

export async function addPaymentMethod(user: User) {
    try {
        const { id: userId } = user;
        const orderId = v4();

        // 1 RUB verification charge for binding
        const payload = {
            amount: 100, // 1 RUB
            order_id: orderId,
            description: 'Card verification',
            email: user.email,
            customerKey: userId,
            success_url: `${window.location.origin}/dashboard/users/account-settings?binding=success`,
            fail_url: `${window.location.origin}/dashboard/users/account-settings?binding=fail`,
            isCardBinding: true
        };

        const initPayment = httpsCallable(functions, 'initTinkoffPayment');
        const response = await initPayment(payload);
        const result = response.data as any;

        if (!result.Success) {
            throw new Error(`Failed to initialize card binding: ${result.Message || result.Details}`);
        }

        return {
            paymentUrl: result.PaymentURL,
        };
    } catch (error) {
        console.error('Error adding payment method:', error);
        throw error;
    }
}

export async function setDefaultPaymentMethod(methodId: string) {
    try {
        const setDef = httpsCallable(functions, 'setDefaultPaymentMethod');
        await setDef({ methodId });
    } catch (error) {
        console.error('Error setting default payment method:', error);
        throw error;
    }
}
